import React, {useEffect, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Grid, IconButton, Menu, MenuItem,
    Paper,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import {Season} from "../../schemas/seasons/seasonsSchema";
import SeasonAutocomplete from "../../components/input/SeasonAutocomplete";
import RacesOverviewTable from "./overview/RacesOverviewTable";
import RacesDetailsList from "./details/RacesDetailsList";
import useSWR from "swr";
import {Race} from "../../schemas/races/racesSchema";
import {getFetcher} from "../../utils/api/fetchers";
import {useAuth} from "../../utils/hooks/useAuth";
import {useNavigate} from "react-router-dom";
import UserRole from "../../schemas/user/roles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {ExpandMore} from "@mui/icons-material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Loading from "../../components/status/Loading";

const RacesOverview: React.FC = () => {
    const {hasPermission} = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [season, setSeason] = useState<Season | null>(null);
    const {data: races} = useSWR<Race[]>(`/seasons/${season?.id}/races`, getFetcher);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [showScrollButton, setShowScrollButton] = useState(false);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    const handleScroll = () => {
        if (window.scrollY > 300) {
            setShowScrollButton(true);
        } else {
            setShowScrollButton(false);
        }
    };

    useEffect(() => {
        window.addEventListener("scroll", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    return (
        <Grid container spacing={2}>
            {/* Left Panel */}
            <Grid item xs={12} sm={12} md={3} sx={{order: {sm: 2, md: 1, xs: 2}}}>
                <Paper elevation={isMobile ? 0 : 3} sx={{p: isMobile ? 0 : 2, px: isMobile ? 1 : 2}}>
                    <SeasonAutocomplete onSeasonChange={setSeason} selectedSeason={season}/>
                    {isMobile ? (
                        <Accordion sx={{mt: 1}}>
                            <AccordionSummary expandIcon={<ExpandMore/>}>
                                <Typography>Přehled závodů</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <RacesOverviewTable races={races}/>
                            </AccordionDetails>
                        </Accordion>
                    ) : (
                        <RacesOverviewTable races={races}/>
                    )}
                </Paper>
            </Grid>

            {/* Center Panel */}
            <Grid item xs={12} sm={12} md={7} sx={{order: {sm: 3, md: 2, xs: 2}}}>
                {!races ? <Loading/> : <RacesDetailsList races={races}/>}
            </Grid>

            {/* Right Panel */
            }
            <Grid item xs={12} sm={12} md={2} sx={{order: {sm: 1, md: 3, xs: 1}}}>
                {isMobile ? (
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography component="h1" variant="h5">Vypsané závody</Typography>
                        {hasPermission(UserRole.ORGANIZER) && (
                            <IconButton
                                title="Více akcí"
                                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                            >
                                <MoreVertIcon/>
                            </IconButton>
                        )}
                        <Menu
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl)}
                            onClose={() => setMenuAnchorEl(null)}
                        >
                            {hasPermission(UserRole.COMMODORE) && (
                                <MenuItem onClick={() => {
                                    navigate("/races");
                                    setMenuAnchorEl(null);
                                }}>
                                    Spravovat závody
                                </MenuItem>
                            )}
                            <MenuItem onClick={() => {
                                navigate("/races/organize");
                                setMenuAnchorEl(null);
                            }}>
                                Pořádat závod
                            </MenuItem>
                        </Menu>
                    </Stack>
                ) : (
                    hasPermission(UserRole.ORGANIZER) && (
                        <Paper elevation={3} sx={{
                            p: 2,
                            position: "sticky",
                            top: theme.spacing(2),
                        }}>
                            <Stack spacing={2}>
                                {hasPermission(UserRole.COMMODORE) && (
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={() => navigate("/races")}
                                    >
                                        Spravovat závody
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => navigate("/races/organize")}
                                >
                                    Pořádat závod
                                </Button>
                            </Stack>
                        </Paper>
                    )
                )}
            </Grid>
            {
                showScrollButton && (
                    <IconButton
                        title="Zpět na začátek"
                        onClick={scrollToTop}
                        sx={{
                            position: "fixed",
                            bottom: 32,
                            right: 32,
                            zIndex: 10,
                            backgroundColor: "primary.main",
                            color: "white",
                            "&:hover": {backgroundColor: "primary.dark"},
                        }}
                    >
                        <ArrowUpwardIcon/>
                    </IconButton>
                )
            }
        </Grid>
    )
        ;
}

export default RacesOverview;